import { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';

const btn = { background: '#4f46e5', color: 'white', border: 0, borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontSize: 14 };
const subtleBtn = { ...btn, background: '#334155' };
const input = { width: '100%', padding: 10, borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: 'white', fontSize: 13 };
const label = { display: 'block', fontSize: 14, color: '#94a3b8', marginBottom: 6 };

export default function QuizFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const quizId = id;
  const isEditing = !!quizId;

  const [form, setForm] = useState({ title: '', description: '', status: 'draft' });
  const [items, setItems] = useState([]);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [domains, setDomains] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDomain, setFilterDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEditing) {
      loadQuizData();
    } else {
      loadQuestions();
    }
  }, [isEditing, quizId]);

  async function loadQuizData() {
    try {
      const [quizData, itemsData, questionRows, catsData, domainsData] = await Promise.all([
        api.quiz(quizId),
        api.quizItems(quizId),
        api.questions(),
        api.categories(),
        api.vtneDomains(),
      ]);
      setForm({ title: quizData.title, description: quizData.description || '', status: quizData.status });
      setItems(itemsData);
      setSelectedQuestionIds(itemsData.map(i => i.question_id));
      setAvailableQuestions(questionRows);
      setCategories(catsData);
      setDomains(domainsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadQuestions() {
    try {
      const [data, catsData, domainsData] = await Promise.all([
        api.questions(),
        api.categories(),
        api.vtneDomains(),
      ]);
      setAvailableQuestions(data);
      setCategories(catsData);
      setDomains(domainsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const filteredQuestions = availableQuestions.filter(q => {
    const matchesSearch = !searchTerm ||
      q.admin_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.stem?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || q.primary_category_id === Number(filterCategory);
    const matchesDomain = !filterDomain || q.vtne_domain_id === Number(filterDomain);
    return matchesSearch && matchesCategory && matchesDomain;
  });

  const toggleQuestion = (id) => {
    if (selectedQuestionIds.includes(id)) {
      setSelectedQuestionIds(prev => prev.filter(qid => qid !== id));
    } else {
      setSelectedQuestionIds(prev => [...prev, id]);
    }
  };

  const selectAllFiltered = () => {
    const newIds = new Set(selectedQuestionIds);
    filteredQuestions.forEach(q => newIds.add(q.id));
    setSelectedQuestionIds(Array.from(newIds));
  };

  const deselectAllFiltered = () => {
    const filteredIds = new Set(filteredQuestions.map(q => q.id));
    setSelectedQuestionIds(selectedQuestionIds.filter(id => !filteredIds.has(id)));
  };

  const allFilteredSelected = filteredQuestions.length > 0 && filteredQuestions.every(q => selectedQuestionIds.includes(q.id));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title) {
      setError('Title is required');
      return;
    }
    if (selectedQuestionIds.length === 0) {
      setError('Select at least one question');
      return;
    }
    setSaving(true);
    try {
      let quiz;
      if (isEditing) {
        quiz = await api.updateQuiz(quizId, {
          title: form.title,
          description: form.description,
          status: form.status,
        });
      } else {
        quiz = await api.createQuiz(form);
      }

      for (const qid of selectedQuestionIds) {
        if (!items.some(i => i.question_id === qid)) {
          await api.addQuizItem(quiz.id, { question_id: qid });
        }
      }

      const currentIds = items.map(i => i.question_id);
      for (const qid of currentIds) {
        if (!selectedQuestionIds.includes(qid)) {
          const item = items.find(i => i.question_id === qid);
          if (item) {
            await api.deleteQuizItem(quiz.id, item.id);
          }
        }
      }

      navigate(`/quizzes/${quiz.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div style={{ padding: 20, maxWidth: 1200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0 }}>{isEditing ? `Edit: ${form.title}` : 'Create New Quiz'}</h1>
        <Link to="/quizzes" style={{ color: '#93c5fd', textDecoration: 'none', fontSize: 14 }}>← Back</Link>
      </div>

      <form onSubmit={handleSubmit} style={{ marginBottom: 30, padding: 20, background: '#161d31', borderRadius: 12, border: '1px solid #334155' }}>
        <div style={{ marginBottom: 15 }}>
          <label style={label}>Quiz Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            style={input}
            required
            autoFocus
          />
        </div>

        <div style={{ marginBottom: 15 }}>
          <label style={label}>Description</label>
          <textarea
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            style={{ ...input, resize: 'vertical', minHeight: 80 }}
            placeholder="Optional notes about this quiz…"
          />
        </div>

        <div style={{ marginBottom: 15 }}>
          <label style={label}>Status</label>
          <select
            value={form.status}
            onChange={e => setForm({ ...form, status: e.target.value })}
            style={input}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {error && <div style={{ color: '#fca5a5', background: '#450a0a', padding: 12, borderRadius: 6, marginBottom: 15 }}>{error}</div>}
        {!form.title && <div style={{ color: '#fbbf24', background: '#78350f', padding: 12, borderRadius: 6, marginBottom: 15, fontSize: 13 }}>⚠ Add a title before saving</div>}
        {selectedQuestionIds.length === 0 && <div style={{ color: '#fbbf24', background: '#78350f', padding: 12, borderRadius: 6, marginBottom: 15, fontSize: 13 }}>⚠ Select at least one question</div>}

        <button type="submit" disabled={saving || !form.title || selectedQuestionIds.length === 0} style={{ ...btn, cursor: (saving || !form.title || selectedQuestionIds.length === 0) ? 'not-allowed' : 'pointer', opacity: (saving || !form.title || selectedQuestionIds.length === 0) ? 0.5 : 1 }}>
          {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Quiz'}
        </button>
      </form>

      <h2 style={{ marginTop: 30, marginBottom: 15 }}>Questions ({selectedQuestionIds.length} selected)</h2>

      <div style={{ marginBottom: 15 }}>
        <input
          type="text"
          placeholder="Search questions by title or stem..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ ...input, marginBottom: 10 }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={label}>Filter by Category</label>
            <select style={input} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
              <option value="">All categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>Filter by Domain</label>
            <select style={input} value={filterDomain} onChange={e => setFilterDomain(e.target.value)}>
              <option value="">All domains</option>
              {domains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {filteredQuestions.length > 0 && (
        <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
          <button
            onClick={selectAllFiltered}
            disabled={allFilteredSelected}
            style={{
              padding: '6px 12px',
              borderRadius: 4,
              border: '1px solid #4f46e5',
              background: allFilteredSelected ? '#1e293b' : 'transparent',
              color: '#4f46e5',
              cursor: allFilteredSelected ? 'default' : 'pointer',
              fontSize: 12,
              opacity: allFilteredSelected ? 0.5 : 1
            }}
          >
            ✓ Select all {filteredQuestions.length > selectedQuestionIds.length ? '(' + (filteredQuestions.length - selectedQuestionIds.filter(id => filteredQuestions.find(q => q.id === id))) + ')' : ''}
          </button>
          {selectedQuestionIds.length > 0 && (
            <button
              onClick={deselectAllFiltered}
              style={{
                padding: '6px 12px',
                borderRadius: 4,
                border: '1px solid #334155',
                background: 'transparent',
                color: '#94a3b8',
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              ✕ Deselect all in search
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10, marginBottom: 20 }}>
        {filteredQuestions.map(q => {
          const isSelected = selectedQuestionIds.includes(q.id);
          return (
            <div
              key={q.id}
              onClick={() => toggleQuestion(q.id)}
              style={{
                padding: 12,
                border: isSelected ? '2px solid #4f46e5' : '1px solid #334155',
                borderRadius: 6,
                cursor: 'pointer',
                background: isSelected ? '#1e293b' : '#0b1120',
                transition: 'all 0.1s'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontWeight: 'bold', flex: 1 }}>{q.admin_title}</span>
                <span style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: isSelected ? '#4f46e5' : '#334155',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: 14,
                  flexShrink: 0,
                  marginTop: 2
                }}>
                  {isSelected ? '✓' : ''}
                </span>
              </div>
              <div style={{ color: '#94a3b8', marginTop: 8, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {q.stem?.substring(0, 120)}
              </div>
            </div>
          );
        })}
      </div>

      {filteredQuestions.length === 0 && (
        <p style={{ color: '#94a3b8', padding: 20, textAlign: 'center', background: '#0b1120', borderRadius: 6 }}>
          {searchTerm ? 'No questions found matching your search.' : 'No questions available.'}
        </p>
      )}
    </div>
  );
}
